import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

import type { AuthUser } from '../types/domain';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { invalidateHomeCollectionStats } from '../services/homeCollectionStatsService';
import { reviewService } from '../services/reviewService';
import { hydrateDemographics } from '../services/demographicsService';

type AuthStatus = 'booting' | 'signed-out' | 'signed-in';

type AuthContextValue = {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  signInWithPassword: (phone: string, password: string) => Promise<void>;
  signInWithCode: (phone: string, authCode: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => apiClient.subscribeSession((session) => {
    const hasSession = Boolean(session.accessToken || session.refreshToken);
    if (!hasSession) {
      reviewService.clearCachesOnSignedOut();
      invalidateHomeCollectionStats();
      setUser(null);
      setStatus('signed-out');
      return;
    }
    if (session.user) {
      setUser(session.user);
      setStatus('signed-in');
    }
  }), []);

  useEffect(() => {
    void (async () => {
      try {
        await apiClient.bootstrap();
        if (!apiClient.hasSession()) {
          setStatus('signed-out');
          return;
        }
        const nextUser = await authService.fetchSession();
        setUser(nextUser);
        setStatus('signed-in');
        void hydrateDemographics(nextUser.id).catch(() => undefined);
      } catch {
        await apiClient.clearSession();
        setUser(null);
        setStatus('signed-out');
      }
    })();
  }, []);

  const signInWithPassword = useCallback(async (phone: string, password: string) => {
    setError(null);
    const nextUser = await authService.loginWithPassword(phone, password);
    setUser(nextUser);
    setStatus('signed-in');
    void hydrateDemographics(nextUser.id).catch(() => undefined);
  }, []);

  const signInWithCode = useCallback(async (phone: string, authCode: string) => {
    setError(null);
    const nextUser = await authService.loginWithAuthCode(phone, authCode);
    setUser(nextUser);
    setStatus('signed-in');
    void hydrateDemographics(nextUser.id).catch(() => undefined);
  }, []);

  const signOut = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setStatus('signed-out');
  }, []);

  const refreshProfile = useCallback(async () => {
    setError(null);
    const nextUser = await authService.fetchSession();
    setUser(nextUser);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, signInWithPassword, signInWithCode, signOut, refreshProfile, clearError }),
    [clearError, error, refreshProfile, signInWithCode, signInWithPassword, signOut, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
